package co.maytes.standupbot.dashboard.components.widgets

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideCheck
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideCopy
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.shared.design.StatusHue
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import kotlinx.browser.window
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.coroutines.await
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import kotlin.js.Promise

private const val COPIED_TEXT = "Copied"
private const val FAILED_TEXT = "Copy failed"
private const val ICON_SIZE_PX = 16
private const val ICON_SWAP_CLASS = "sb-icon-swap"

private class LastHint(var text: String = COPIED_TEXT)

@Composable
fun CopyButton(
    text: String,
    modifier: Modifier = Modifier,
    ariaLabel: String = "Copy to clipboard",
    size: ControlSize = ControlSize.Md,
) {
    val scope = rememberCoroutineScope()
    var hint by remember { mutableStateOf<String?>(null) }
    var copyCount by remember { mutableStateOf(0) }
    val lastHint = remember { LastHint() }
    hint?.let { lastHint.text = it }

    LaunchedEffect(copyCount) {
        if (copyCount == 0) return@LaunchedEffect
        delay(Motion.COPIED_HINT_MS)
        hint = null
    }

    val copied = hint == COPIED_TEXT

    Div(
        attrs = Modifier
            .styleModifier {
                property("position", "relative")
                property("display", "inline-flex")
            }
            .then(modifier)
            .toAttrs(),
    ) {
        IconButton(
            ariaLabel = ariaLabel,
            size = size,
            tooltip = null,
            onClick = {
                scope.launch {
                    hint = if (writeToClipboard(text)) COPIED_TEXT else FAILED_TEXT
                    copyCount++
                }
            },
        ) {
            Div(attrs = Modifier.styleModifier { property("display", "inline-grid") }.toAttrs()) {
                IconSwapLayer(visible = !copied) { LucideCopy(size = ICON_SIZE_PX) }
                IconSwapLayer(visible = copied) {
                    LucideCheck(size = ICON_SIZE_PX, strokeColor = Palette.status(StatusHue.Success).solid.cssRgb())
                }
            }
        }
        TooltipBubble(text = lastHint.text, placement = TooltipPlacement.Top, open = hint != null)
        Span(
            attrs = Modifier
                .styleModifier {
                    property("position", "absolute")
                    property("width", "1px")
                    property("height", "1px")
                    property("overflow", "hidden")
                    property("clip", "rect(0 0 0 0)")
                    property("white-space", "nowrap")
                }
                .toAttrs {
                    attr("role", "status")
                    attr("aria-live", "polite")
                },
        ) {
            Text(hint.orEmpty())
        }
    }
}

@Composable
private fun IconSwapLayer(visible: Boolean, content: @Composable () -> Unit) {
    val base = Modifier
        .classNames(ICON_SWAP_CLASS)
        .styleModifier {
            property("grid-area", "1 / 1")
            property("display", "inline-flex")
        }
    Div(attrs = (if (visible) base.classNames(Motion.OPEN_CLASS) else base).toAttrs()) {
        content()
    }
}

private suspend fun writeToClipboard(text: String): Boolean = try {
    val clipboard = window.navigator.asDynamic().clipboard
    if (clipboard == null || clipboard == js("undefined")) {
        false
    } else {
        @Suppress("UNCHECKED_CAST")
        (clipboard.writeText(text) as Promise<Unit>).await()
        true
    }
} catch (_: Throwable) {
    false
}
